import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import Description from 'components/CareSummary/Description';
import Empty from 'components/CareSummary/Empty';
import Title from 'components/CareSummary/Title';
import { LineChart } from 'components/Graphs/LineChart';
import { Container } from 'shared-components/Container';
import { useGetQualityOfLifeScoreGraphQuery } from 'store/api/pkd/endpoints/graphs';
import {
  useGetProfileParticipantTasksQuery,
  useGetProfileParticipantTaskQuery,
} from 'store/api/pkd/endpoints/participantTask';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { TaskTitles } from 'utils/enum';
import { ChartGraphSkeleton } from '../ChartSkeleton';
import { parseQOLOverTimeGraph } from './utils';

const QualityOfLifeScoresOverTimeChart = () => {
  const theme = useTheme();

  const {
    isPersonLoading,
    isPersonAttributesFetching,
    person,
    personId,
    personAccountProfileId,
  } = useGetPersonAndAttributes();

  const { participantTask, isParticipantTaskLoading } =
    useGetProfileParticipantTasksQuery(
      { personAccountProfileId },
      {
        skip             : !personAccountProfileId,
        selectFromResult : ({ data = [], isFetching }) => ({
          participantTask          : data,
          isParticipantTaskLoading : isFetching,
        }),
      }
    );
  const coreQuestionnaireTaskId = participantTask?.find(
    (item) => item.taskTitle === TaskTitles.coreQuestionnaire
  )?.id;

  const { data: coreQuestionnaire, isLoading: isCoreQuestionnaireLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: coreQuestionnaireTaskId },
      { skip: !coreQuestionnaireTaskId }
    );
  const coreQuestionnaireData = coreQuestionnaire;

  const {
    data: qualityOfLifeSOTGraph,
    isFetching: isQualityOfLifeGraphLoading,
  } = useGetQualityOfLifeScoreGraphQuery({ personId }, { skip: !personId });
  const qualityOfLifeSOTData = parseQOLOverTimeGraph(qualityOfLifeSOTGraph);

  const ADPKDISTaskId = participantTask?.find(
    (item) => item.taskTitle === TaskTitles.ADPKDIS
  )?.id;

  const { data: ADPKDIS, isLoading: isADPKDISLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: ADPKDISTaskId },
      { skip: !ADPKDISTaskId }
    );

  const adpkdISData = ADPKDIS;

  const qualityOfLifeSOT = {
    xAxis : {
      type : 'category',
      data : qualityOfLifeSOTData?.date,
    },
    legend : {
      data : [
        'Physical burden score',
        'Emotional burden score',
        'Fatigue burden score',
      ],
      icon : 'circle',
    },
    yAxis : {
      type : 'value',
      min  : 1,
      max  : 5,
    },
    series : [
      {
        name      : 'Physical burden score',
        data      : qualityOfLifeSOTData?.physicalBurdenScore,
        type      : 'line',
        itemStyle : { color: theme.palette.primary.blue1 },
        lineStyle : { color: theme.palette.primary.blue1 },
      },
      {
        name      : 'Emotional burden score',
        data      : qualityOfLifeSOTData?.emotionalBurdenScore,
        type      : 'line',
        itemStyle : { color: theme.palette.primary.orange },
        lineStyle : { color: theme.palette.primary.orange },
      },
      {
        name      : 'Fatigue burden score',
        data      : qualityOfLifeSOTData?.fatigueBurdenScore,
        type      : 'line',
        itemStyle : { color: theme.palette.primary.purple },
        lineStyle : { color: theme.palette.primary.purple },
      },
    ],
  };

  const isLoading =
    isPersonLoading ||
    isParticipantTaskLoading ||
    isQualityOfLifeGraphLoading ||
    isCoreQuestionnaireLoading ||
    isADPKDISLoading;
  const difficulties = [
    'not difficult',
    'a little',
    'somewhat',
    'very',
    'extremely',
  ];

  const content = isLoading
    ? (
      <ChartGraphSkeleton />
    )
    : qualityOfLifeSOT?.xAxis?.data?.length === 0 ||
    !adpkdISData?.taskEntry
      ? (
        <Empty
          description='Fill out the ADPKD-IS module'
          to={`/questionnaire/${adpkdISData?.participantTaskId}`}
          buttonText='Fill out the ADPKD-IS module'
          hideAction={
            !adpkdISData ||
        !coreQuestionnaireData?.taskEntry ||
        (Boolean(coreQuestionnaireData?.taskEntry) && ADPKDIS?.length === 0)
          }
        />
      )
      : (
        <Box>
          <LineChart
            id='quality-of-life-over-time'
            width='550px'
            height='500px'
            option={qualityOfLifeSOT}
          />
          <Box
            sx={{
              display    : 'flex',
              alignItems : 'center',
              flexWrap   : 'wrap',
              gap        : '5px',
            }}>
            <Typography
              variant='body2'>
              <b>Difficulty: </b>
            </Typography>
            {difficulties.map((difficulty, index) => (
              <Typography
                key={index} sx={{ ml: '8px', fontSize: '13px' }}>
                <b>{index + 1} </b>
                <span
                  style={{ opacity: '0.5' }}>{difficulty}</span>
              </Typography>
            ))}
          </Box>
        </Box>
      );

  return (
    <Container>
      <Title>Quality of life scores over time</Title>
      <Description>
        As your PKD progresses, you may see changes in your quality of life and
        the impact it has on your life. Share details of your pain, fatigue, or
        other factors with your care team.
      </Description>
      {content}
    </Container>
  );
};

export default QualityOfLifeScoresOverTimeChart;
